/* eslint-disable import/no-extraneous-dependencies */
import axios from "axios";

// ─── Constants ────────────────────────────────────────────────────────────────
const apiUrl = process.env.REACT_APP_API_URL;
const loginUrl = `${apiUrl}/auth/login`;
const refreshTokenUrl = `${apiUrl}/auth/refresh/token`;
const adminOrigin = process.env.REACT_APP_ADMIN ?? "*";

// ─── Token helpers ────────────────────────────────────────────────────────────
const TokenService = {
  getAccessToken: () => localStorage.getItem("token"),
  getRefreshToken: () => localStorage.getItem("refreshToken"),
  setTokens: (accessToken, refreshToken) => {
    localStorage.setItem("token", accessToken);
    if (refreshToken) sessionStorage.setItem("refreshToken", refreshToken);
  },
  clearTokens: () => {
    localStorage.clear();
    sessionStorage.clear();
  }
};

// ─── Interceptor state ────────────────────────────────────────────────────────
let isRefreshing = false; // true while a refresh call is in-flight
let isLoggingOut = false; // true once logout has been triggered — blocks duplicate logouts
let failedQueue = [];

// ─── Helpers ──────────────────────────────────────────────────────────────────
const processQueue = (error, token = null) => {
  failedQueue.forEach(({ resolve, reject }) => {
    if (error) reject(error);
    else resolve(token);
  });
  failedQueue = [];
};

/**
 * Clears tokens, notifies the parent frame, and flips the flag so no further
 * 401 handling (for queued or replayed requests) triggers a second logout.
 */
const logout = () => {
  if (isLoggingOut) return; // ✅ already logging out — do nothing
  isLoggingOut = true;

  TokenService.clearTokens();
  window.top?.postMessage({ type: "logout" }, adminOrigin);
};

// ─── Axios instance ────────────────────────────────────────────────────────────
const http = axios.create({ baseURL: apiUrl });

// ─── Request interceptor ───────────────────────────────────────────────────────
http.interceptors.request.use(
  (config) => {
    const token = TokenService.getAccessToken();
    if (token) config.headers.Authorization = `Bearer ${token}`;
    return config;
  },
  (error) => Promise.reject(error)
);

// ─── Response interceptor ─────────────────────────────────────────────────────
http.interceptors.response.use(
  (response) => response,

  async (error) => {
    const originalRequest = error.config;
    const status = error.response?.status;

    // ── Not a 401 or no config → pass through ─────────────────────────────────
    if (status !== 401 || !originalRequest) {
      return Promise.reject(error);
    }

    // ── Already logging out → silently reject, no further action ──────────────
    // Covers every queued request that gets rejected after logout fires
    if (isLoggingOut) {
      return Promise.reject(error);
    }

    // ── 401 on login → pass through ───────────────────────────────────────────
    if (originalRequest.url === loginUrl) {
      return Promise.reject(error);
    }

    // ── 401 on the refresh endpoint → discard queue + logout ──────────────────
    if (originalRequest.url === refreshTokenUrl) {
      processQueue(error, null);
      logout();
      return Promise.reject(error);
    }

    // ── Already retried → pass through ────────────────────────────────────────
    // _retry is ONLY set on the request that owns the refresh,
    // never on queued requests, so they can be replayed correctly
    if (originalRequest._retry) {
      return Promise.reject(error);
    }

    // ── No tokens → logout immediately ────────────────────────────────────────
    const accessToken = TokenService.getAccessToken();
    const refreshToken = TokenService.getRefreshToken();

    if (!accessToken || !refreshToken) {
      logout();
      return Promise.reject(error);
    }

    // ── Refresh already in-flight → queue this request ────────────────────────
    if (isRefreshing) {
      return new Promise((resolve, reject) => {
        failedQueue.push({ resolve, reject });
      })
        .then((newToken) => {
          originalRequest.headers.Authorization = `Bearer ${newToken}`;
          return http(originalRequest);
        })
        .catch((err) => Promise.reject(err));
    }

    // ── First 401 → own the refresh ───────────────────────────────────────────
    originalRequest._retry = true;
    isRefreshing = true;

    try {
      const { data, status: refreshStatus } = await http.post(refreshTokenUrl, {
        accessToken,
        refreshToken
      });

      if (refreshStatus !== 200 || !data?.accessToken) {
        throw new Error("Invalid refresh response");
      }

      const { accessToken: newAccess } = data;

      TokenService.setTokens(newAccess);
      http.defaults.headers.common.Authorization = `Bearer ${newAccess}`;
      window.top?.postMessage(
        { type: "updateAccessToken", token: newAccess },
        adminOrigin
      );

      processQueue(null, newAccess); // unblock queued requests

      originalRequest.headers.Authorization = `Bearer ${newAccess}`;
      return http(originalRequest); // replay the original
    } catch (refreshError) {
      processQueue(refreshError, null); // reject queued requests
      logout(); // single logout call
      return Promise.reject(refreshError);
    } finally {
      isRefreshing = false;
    }
  }
);

// ─── Exports ──────────────────────────────────────────────────────────────────
export default {
  get: http.get.bind(http),
  post: http.post.bind(http),
  put: http.put.bind(http),
  patch: http.patch.bind(http),
  delete: http.delete.bind(http),
  http
};
